import { create } from "zustand";

export interface UserRights {
  user_id: number;
  right_to_share: boolean;
  right_to_read: boolean;
  right_to_write: boolean;
  right_to_duplicate: boolean;
}

const DEFAULT_USER_RIGHTS: UserRights = {
  user_id: 0,
  right_to_share: false,
  right_to_read: false,
  right_to_write: false,
  right_to_duplicate: false,
};

export interface EditorState {
  activeSection: string | null;
  selectedItemId: number | null;
  expandedNodes: Set<string>;
  loadingNodes: Set<string>;
  searchInput: string;
  searchOutput: string;
  viewMode: "overview" | "section";
  userRights: UserRights;
  scenarioId: number | null;
}

export interface EditorActions {
  setActiveSection: (section: string | null) => void;
  setSelectedItem: (id: number | null) => void;
  toggleNode: (id: string) => void;
  setNodeExpanded: (id: string, expanded: boolean) => void;
  bulkSetExpanded: (entries: Array<[string, boolean]>) => void;
  clearExpandedNodes: () => void;
  setNodeLoading: (id: string, loading: boolean) => void;
  isNodeLoading: (id: string) => boolean;
  setSearch: (kind: "Input" | "Output", query: string) => void;
  setViewMode: (mode: "overview" | "section") => void;
  setUserRights: (rights: UserRights) => void;
  setScenarioId: (id: number | null) => void;
}

export type EditorStore = EditorState & EditorActions;

export const useEditorStore = create<EditorStore>((set) => ({
  activeSection: null,
  selectedItemId: null,
  expandedNodes: new Set<string>(),
  loadingNodes: new Set<string>(),
  searchInput: "",
  searchOutput: "",
  viewMode: "overview",
  userRights: DEFAULT_USER_RIGHTS,
  scenarioId: null,

  setActiveSection: (section) => set({ activeSection: section }),

  setSelectedItem: (id) => set({ selectedItemId: id }),

  toggleNode: (id) =>
    set((state) => {
      const next = new Set(state.expandedNodes);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return { expandedNodes: next };
    }),

  setNodeExpanded: (id, expanded) =>
    set((state) => {
      const next = new Set(state.expandedNodes);
      if (expanded) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return { expandedNodes: next };
    }),

  bulkSetExpanded: (entries) =>
    set((state) => {
      const next = new Set(state.expandedNodes);
      for (const [id, expanded] of entries) {
        if (expanded) {
          next.add(id);
        } else {
          next.delete(id);
        }
      }
      return { expandedNodes: next };
    }),

  clearExpandedNodes: () => set({ expandedNodes: new Set<string>() }),

  setNodeLoading: (id, loading) =>
    set((state) => {
      const next = new Set(state.loadingNodes);
      if (loading) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return { loadingNodes: next };
    }),

  isNodeLoading: (id) => useEditorStore.getState().loadingNodes.has(id),

  setSearch: (kind, query) =>
    set(kind === "Input" ? { searchInput: query } : { searchOutput: query }),

  setViewMode: (mode) => set({ viewMode: mode }),

  setUserRights: (rights) => set({ userRights: rights }),

  setScenarioId: (id) => set({ scenarioId: id }),
}));
